import { useFormikContext } from "formik";
import FormField from "layouts/pages/users/new-user/components/FormField";
import type { CupomFormValues } from "./schemas/initialValues";
import type { TipoDesconto } from "interfaces/loja";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Autocomplete from "@mui/material/Autocomplete";
import Switch from "@mui/material/Switch";

export const FORM_ID = "form-cupom-loja";

const TIPO_OPCOES: TipoDesconto[] = ["PORCENTAGEM", "VALOR_FIXO"];
const TIPO_LABEL: Record<TipoDesconto, string> = {
  PORCENTAGEM: "Percentual",
  VALOR_FIXO: "Valor fixo",
};

export default function CupomFormDados() {
  const { values, handleBlur, setFieldValue } =
    useFormikContext<CupomFormValues>();

  const valorLabel =
    values.tipo === "PORCENTAGEM" ? "Percentual (%)" : "Valor (R$)";

  return (
    <MDBox display="flex" flexDirection="column" gap={2} mt={2}>
      <FormField
        name="codigo"
        label="Código do cupom"
        variant="outlined"
        required
        placeholder="Ex: PROMO20 (sem espaços)"
        inputProps={{ style: { textTransform: "uppercase" }, maxLength: 64 }}
      />
      <MDBox
        display="grid"
        gap={2}
        sx={{ gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}
      >
        <Autocomplete
          value={values.tipo || null}
          options={TIPO_OPCOES}
          getOptionLabel={(option) => TIPO_LABEL[option] ?? option}
          isOptionEqualToValue={(option, value) => option === value}
          onChange={(_, newValue) =>
            setFieldValue("tipo", newValue ?? "")
          }
          onBlur={() => handleBlur("tipo")}
          renderInput={(params) => (
            <FormField
              {...params}
              variant="outlined"
              value={values.tipo}
              name="tipo"
              required
              label="Tipo de desconto"
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
        <FormField
          name="valor"
          label={valorLabel}
          type="number"
          variant="outlined"
          required
          inputProps={{
            min: values.tipo === "PORCENTAGEM" ? 1 : 0,
            max: values.tipo === "PORCENTAGEM" ? 100 : undefined,
            step: values.tipo === "PORCENTAGEM" ? 1 : 0.01,
          }}
        />
      </MDBox>
      <MDBox
        display="grid"
        gap={2}
        sx={{ gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}
      >
        <FormField
          name="inicioEm"
          label="Data início"
          type="date"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
        />
        <FormField
          name="fimEm"
          label="Data fim"
          type="date"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
        />
      </MDBox>
      <MDBox display="flex" alignItems="center" gap={1.5}>
        <MDTypography variant="caption" color="text" fontWeight="medium">
          Situação do cupom
        </MDTypography>
        <Switch
          checked={values.ativo}
          onChange={(_, c) => setFieldValue("ativo", c)}
          color="primary"
        />
        <MDTypography variant="button">Ativo</MDTypography>
      </MDBox>
      <MDBox>
        <MDTypography
          variant="caption"
          color="text"
          fontWeight="medium"
          sx={{ display: "block", mb: 2 }}
        >
          Opcionais
        </MDTypography>
        <MDBox
          display="grid"
          gap={2}
          sx={{
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
            },
          }}
        >
          <FormField
            name="subtotalMinimo"
            label="Subtotal mínimo (R$)"
            type="number"
            variant="outlined"
            inputProps={{ min: 0, step: 0.01 }}
          />
          <FormField
            name="limiteUsoGlobal"
            label="Limite de uso global"
            type="number"
            variant="outlined"
            inputProps={{ min: 0, step: 1 }}
          />
          <FormField
            name="limiteUsoPorUsuario"
            label="Limite por usuário"
            type="number"
            variant="outlined"
            inputProps={{ min: 0, step: 1 }}
          />
        </MDBox>
      </MDBox>
    </MDBox>
  );
}
